/* eslint-disable react/display-name */
import styled from 'styled-components';
import {
  ExpressionType,
  type LogicalMixinExpression,
  type PureProductExpression,
  type CombinationExpression,
  type ForIndustryExpression,
  type ForSpecialEndUseExpression,
  CertificateExpression,
  RecyclabilityExpression,
  UsingEnergySourceExpression,
} from '@upright/common/model/input/companies/products/ipr/types';

export function transformToJsx(op: LogicalMixinExpression, level: number): string {
  const operandType = op.type;
  const transformer = getTransformer(operandType, level);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!transformer) {
    throw new Error(`No transformer for operand type ${operandType}`);
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  return (transformer as any)(op);
}

function getTransformer<T extends ExpressionType>(type: T, level: number) {
  const style = {
    backgroundColor: getBackgroundColorByLevel(level),
    color: getTextColorByLevel(level),
  };
  const nextLevel = level + 1;

  switch (type) {
    case ExpressionType.PureProduct:
      return (op: PureProductExpression) => <Block style={style}>{op.title}</Block>;
    case ExpressionType.Combination:
      return (op: CombinationExpression) => (
        <HorizontalBlock style={style}>
          {op.combinations.map((c, index) => {
            const block = transformToJsx(c, nextLevel);
            if (index < op.combinations.length - 1) {
              return (
                <>
                  {block} <Operator>and</Operator>
                </>
              );
            }
            return block;
          })}
        </HorizontalBlock>
      );
    case ExpressionType.ForIndustry:
      return (op: ForIndustryExpression) => (
        <Block style={style}>
          {transformToJsx(op.baseProduct, nextLevel)} <Operator>for</Operator>{' '}
          {transformToJsx(op.forProduct, nextLevel)}
        </Block>
      );
    case ExpressionType.Certificate:
      return (op: CertificateExpression) => (
        <Block style={style}>
          <Operator>{op.certificate}-certified</Operator> {transformToJsx(op.baseProduct, nextLevel)}
        </Block>
      );
    case ExpressionType.Recyclability:
      return (op: RecyclabilityExpression) => (
        <Block style={style}>
          <Operator>{op.recyclability}</Operator> {transformToJsx(op.baseProduct, nextLevel)}
        </Block>
      );
    case ExpressionType.UsingEnergySource:
      return (op: UsingEnergySourceExpression) => (
        <Block style={style}>
          {transformToJsx(op.baseProduct, nextLevel)} <Operator>using</Operator>{' '}
          {transformToJsx(op.usingEnergySource, nextLevel)}
        </Block>
      );
    case ExpressionType.ForSpecialEndUse:
      return (op: ForSpecialEndUseExpression) => (
        <Block style={style}>
          {transformToJsx(op.baseProduct, nextLevel)} <Operator>for</Operator>{' '}
          {transformToJsx(op.forSpecialEndUse, nextLevel)}
        </Block>
      );
    // `ForDisabledPeople` has no block form, so it lands on the same throw as an unknown type.
    case ExpressionType.ForDisabledPeople:
    default:
      throw new Error(`No transformer for operand type ${type}`);
  }
}

function getTextColorByLevel(level: number) {
  if (level > 4) {
    return 'var(--color__white)';
  }
  return 'var(--platform-gray-black)';
}

function getBackgroundColorByLevel(level: number) {
  switch (level) {
    case 0:
      return 'var(--color__blue-100)';
    case 1:
      return 'var(--platform-blue-200)';
    case 2:
      return 'var(--platform-blue-300)';
    case 3:
      return 'var(--platform-blue-400)';
    case 4:
      return 'var(--platform-blue-500)';
    case 5:
      return 'var(--platform-blue-600)';
    case 6:
      return 'var(--platform-blue-700)';
    case 7:
      return 'var(--platform-blue-800)';
    case 8:
      return 'var(--platform-blue-900)';
    case 9:
    default:
      return 'var(--platform-blue-900)';
  }
}

const Block = styled.div`
  /*border: 1px solid var(--color__gray-300);*/
  font-size: var(--font-size__md);
  display: flex;
  flex-direction: column;
  min-width: 100px;
  align-items: center;
  justify-content: center;
  padding: var(--space__sm);
  gap: var(--space__sm);
  border-radius: var(--border-radius__md);
  text-align: center;
`;

const HorizontalBlock = styled(Block)`
  flex-direction: row;
`;

const Operator = styled.div`
  background-color: var(--platform-purple-700);
  color: white;
  border-radius: var(--border-radius__md);
  font-weight: 700;
  padding-top: var(--space__xs);
  padding-bottom: var(--space__xs);
  padding-left: var(--space__sm);
  padding-right: var(--space__sm);
  text-align: center;
`;
